import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import useConfig from "../../hooks/useConfig";
import HttpService from "../../hooks/Https-services";
import PageRoutes, { APIRoutes } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import SentGifts from "./SentGifts";
import useCommonDetails from "../../Common/Hooks/useCommonDetails";

// HTPTV-636: display copy only (labels + descriptions). The available plan tiers
// and all prices come from the backend (/gift-subscription/options), so nothing
// about pricing is hardcoded here and the shown total always matches the charge.
const TIER_META: Record<string, { label: string; description: string }> = {
  basic: {
    label: "Basic",
    description: "Every game, live & on-demand,\nacross all three leagues.",
  },
  insider: {
    label: "Insider",
    description:
      "Everything in Basic, plus personalized\nchannels, watch parties & clip downloads.",
  },
};

const tierLabel = (tier: string) =>
  TIER_META[tier]?.label ||
  (tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : "");

type BillingCycle = "monthly" | "annual";

const GiftPage = () => {
  const configData = useConfig();
  const navigate = useNavigate();
  const currentUser = useCommonDetails();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const primary = configData?.primary;
  const tertiary = configData?.tertiary;

  const [isLoadingData, setIsLoading] = useState(false);
  const [billing, setBilling] = useState<BillingCycle>("annual");
  const [selectedPlan, setSelectedPlan] = useState<string>("insider");
  const [termCount, setTermCount] = useState<number>(1);
  const [giftOptions, setGiftOptions] = useState<any[]>([]);
  const [termMax, setTermMax] = useState<number>(24);
  // HTPTV-636: when the recipient already has an active subscription, the gift is
  // applied as account credit (not a redeemable subscription). Warn the buyer.
  const [recipientHasSub, setRecipientHasSub] = useState(false);
  // HTPTV-636: the displayed total must match the actual charge, so checkout is
  // gated on a successful load of the real Stripe prices. pricesReady=false keeps
  // the button disabled; pricesError surfaces a retry instead of charging at a
  // stale fallback price.
  const [pricesReady, setPricesReady] = useState(false);
  const [pricesError, setPricesError] = useState(false);

  // HTPTV-636: the plan tiers to show come from the backend options (known tiers
  // first, then any others). Prices are read straight from the options and never
  // hardcoded, so the displayed total always matches the Stripe charge.
  const availableTiers = useMemo(() => {
    const order = ["basic", "insider"];
    const rank = (t: string) => {
      const i = order.indexOf(t);
      return i === -1 ? order.length : i;
    };
    return Array.from(new Set(giftOptions.map((o) => o.tier))).sort(
      (a, b) => rank(a) - rank(b)
    );
  }, [giftOptions]);

  const activeLabel = tierLabel(selectedPlan);
  const priceFor = (tier: string, b: BillingCycle) => {
    const opt = giftOptions.find((o) => o.tier === tier && o.billing === b);
    return opt ? Number(opt.priceInCurrency) / 100 : 0;
  };
  const unitPrice = priceFor(selectedPlan, billing);
  const planPrice = unitPrice * termCount;

  // Keep the selection valid once options load (in case the default tier isn't
  // offered by the backend for this instance).
  useEffect(() => {
    if (availableTiers.length && !availableTiers.includes(selectedPlan)) {
      setSelectedPlan(availableTiers[0]);
    }
  }, [availableTiers, selectedPlan]);

  // HTPTV-636: load gift-subscription options (real Stripe prices + term bounds).
  // Retries a few times on a transient failure; only marks prices ready once the
  // backend options actually load, so we never let the buyer check out against
  // the stale built-in fallback prices (which differ from the Stripe charge).
  const loadOptions = useCallback(async () => {
    setPricesError(false);
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await HttpService.get(APIRoutes.GiftSubscriptionOptions);
        const data = res?.data?.data;
        if (Array.isArray(data?.options) && data.options.length) {
          setGiftOptions(data.options);
          if (data?.termMax) setTermMax(Number(data.termMax));
          setPricesReady(true);
          return;
        }
      } catch (e) {
        // retry below
      }
      await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
    }
    setPricesError(true);
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // HTPTV-636: ask the backend whether the recipient already subscribes, so we
  // can tell the buyer the gift will be applied as account credit. Best-effort;
  // never blocks the form. The backend re-checks authoritatively at payment.
  const checkRecipientStatus = async (rawEmail: string) => {
    const email = (rawEmail || "").trim();
    const valid = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
    const isSelf =
      currentUser?.Email &&
      email.toLowerCase() === currentUser.Email.trim().toLowerCase();
    if (!valid || isSelf) {
      setRecipientHasSub(false);
      return;
    }
    try {
      const res = await HttpService.get(
        `${APIRoutes.GiftSubscriptionRecipientStatus}?email=${encodeURIComponent(email)}`
      );
      setRecipientHasSub(Boolean(res?.data?.data?.hasActiveSubscription));
    } catch {
      setRecipientHasSub(false);
    }
  };

  const validate = (values: any) => {
    const errors: any = {};
    if (!values.name) errors.name = "This field is required";
    if (!values.email) {
      errors.email = "This field is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = "Enter a valid email address";
    } else if (
      currentUser?.Email &&
      values.email.trim().toLowerCase() === currentUser.Email.trim().toLowerCase()
    ) {
      // HTPTV-636: a gift is for someone else; buying for yourself is the normal
      // subscription flow. The backend enforces this too.
      errors.email = "You can't gift to your own email. Buy a plan for yourself instead.";
    }
    if (!values.date) errors.date = "This field is required";
    if (!values.message) errors.message = "This field is required";
    return errors;
  };

  const formik: any = useFormik({
    initialValues: {
      name: "",
      email: "",
      send: false,
      date: null as any,
      message: "",
    },
    validate,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        // HTPTV-636: gift-subscription flow (one-time Invoice + PaymentElement).
        const res = await HttpService.post(APIRoutes.CreateGiftSubscription, {
          planTier: selectedPlan,
          billingModel: billing,
          termCount,
          recipientName: values.name,
          recipientEmail: values.email,
          deliveryDate: values.date ? dayjs(values.date).toISOString() : null,
          sendNow: values.send,
          message: values.message,
        });
        // HTPTV-636: the buyer's account credit fully covered the gift, so the
        // backend already charged $0 and marked it paid. There is no payment to
        // collect, so skip the payment page.
        if (res?.data?.data?.alreadyPaid || !res?.data?.data?.clientSecret) {
          toast.success("Your gift is all set - it was covered by your account credit.");
          navigate(PageRoutes.HomePage);
          setIsLoading(false);
          return;
        }
        if (res) {
          const encodedStripeLink = encodeURIComponent(res.data.data.clientSecret);
          const encodedPaymentId = encodeURIComponent(res.data.data.plan_id);
          const encodedSubscriptionId = encodeURIComponent(res.data.data.paymentId);
          navigate(
            PageRoutes.paymentPage
              .replace(":clientSecret", encodedStripeLink)
              .replace(":plan_id", encodedPaymentId)
              .replace(":paymentId", encodedSubscriptionId) +
              `?gt=${planPrice.toFixed(2)}`
          );
        }
        setIsLoading(false);
      } catch (error: any) {
        const data = error?.response?.data;
        if (data?.responseType === "self_gift") {
          formik.setFieldError(
            "email",
            "You can't gift to your own email. Buy a plan for yourself instead."
          );
          toast.error(
            data?.message ||
              "You can't gift a subscription to your own email."
          );
        } else {
          toast.error(data?.message ?? "Something went wrong");
        }
        setIsLoading(false);
      }
    },
  });

  const sectionCard = (num: number, title: string, children: React.ReactNode) => (
    <Paper
      elevation={0}
      sx={{ borderRadius: "16px", mb: 2.5, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
    >
      <Box sx={{ px: { xs: 2.5, md: 3 }, pt: { xs: 2.5, md: 3 }, pb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
          <Box sx={{
            width: 26, height: 26, borderRadius: "50%", bgcolor: primary,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: tertiary, lineHeight: 1 }}>{num}</Typography>
          </Box>
          <Typography variant="body2" fontWeight={700} color="text.secondary" letterSpacing={1.2} lineHeight={1} textTransform="none">
            {title}
          </Typography>
        </Stack>
        {children}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "action.hover", pb: { xs: 12, md: 4 }, "& *": { fontStyle: "normal !important" } }}>
      <Box sx={{ p: 4, px: { xs: 4, sm: 4, md: 8 }, pt: { xs: 3, md: 4 } }}>

        {/* Page header */}
        <Box sx={{ mb: 3 }}>
          <Typography fontWeight={800} sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, lineHeight: 1.2, color: primary, letterSpacing: "-0.5px", textTransform: "none" }}>
            Gift a season of {configData.personalChannel}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1} sx={{ maxWidth: 520, textTransform: "none" }}>
            Give the {configData.personalChannel} fan in your life live and on-demand access to every game.
            Perfect for friends and family who can't make it to the rink.
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="flex-start">

            {/* LEFT COLUMN */}
            <Grid item xs={12} md={8}>

              {/* Step 1: Choose the plan */}
              {sectionCard(1, "CHOOSE THE PLAN", (
                <>
                      {/* Billing toggle */}
                      <Box sx={{ display: "block", mb: 2 }}>
                      <ToggleButtonGroup
                        exclusive
                        value={billing}
                        onChange={(_, val) => { if (val) setBilling(val as BillingCycle); }}
                        sx={{ border: `1px solid ${primary}`, borderRadius: "8px", overflow: "hidden" }}
                      >
                        {(["monthly", "annual"] as BillingCycle[]).map((b) => (
                          <ToggleButton
                            key={b}
                            value={b}
                            sx={{
                              px: 3, py: 0.75, border: "none", fontWeight: 700,
                              fontSize: "0.875rem", textTransform: "none", letterSpacing: 0,
                              color: primary,
                              bgcolor: "background.paper",
                              "&.Mui-selected": {
                                bgcolor: primary,
                                color: tertiary,
                                "&:hover": { bgcolor: primary },
                              },
                              "&:hover": { bgcolor: `${primary}14` },
                            }}
                          >
                            {b === "monthly" ? "Monthly" : "Annual"}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                      </Box>

                      {/* Term length */}
                      <Box sx={{ display: "block", mb: 2 }}>
                        <TextField
                          type="number"
                          size="small"
                          label={`Number of ${billing === "monthly" ? "months" : "years"}`}
                          value={termCount}
                          inputProps={{ min: 1, max: termMax }}
                          onChange={(e) => {
                            const v = Math.max(1, Math.min(termMax, Math.floor(Number(e.target.value) || 1)));
                            setTermCount(v);
                          }}
                          sx={{ maxWidth: 240 }}
                        />
                      </Box>

                      {/* Plan cards - tiers/prices come from the backend; show a
                          skeleton while they load and a retry if the load fails. */}
                      {pricesError ? (
                        <Box sx={{ py: 2 }}>
                          <Typography variant="body2" color="error" sx={{ textTransform: "none" }}>
                            Couldn't load plans.{" "}
                            <Box component="span" onClick={loadOptions} sx={{ textDecoration: "underline", cursor: "pointer", fontWeight: 700 }}>
                              Retry
                            </Box>
                          </Typography>
                        </Box>
                      ) : !pricesReady ? (
                        <Grid container spacing={2}>
                          {[0, 1].map((i) => (
                            <Grid item xs={12} sm={6} key={i}>
                              <Skeleton variant="rounded" height={150} sx={{ borderRadius: "12px" }} />
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Grid container spacing={2}>
                          {availableTiers.map((tier) => {
                            const isSel = selectedPlan === tier;
                            return (
                              <Grid item xs={12} sm={6} key={tier}>
                                <Box
                                  onClick={() => setSelectedPlan(tier)}
                                  sx={{
                                    p: 2.5, borderRadius: "12px", height: "100%",
                                    border: "2px solid",
                                    borderColor: isSel ? primary : "divider",
                                    bgcolor: isSel ? `${primary}08` : "background.paper",
                                    cursor: "pointer",
                                    transition: "border-color 150ms ease-out, background-color 150ms ease-out, box-shadow 150ms ease-out",
                                    "&:hover": { borderColor: primary, boxShadow: `0 0 0 4px ${primary}14` },
                                    position: "relative",
                                  }}
                                >
                                  <Box sx={{
                                    position: "absolute", top: 10, right: 10,
                                    opacity: isSel ? 1 : 0, transform: isSel ? "scale(1)" : "scale(0.5)",
                                    transition: "opacity 150ms ease-out, transform 150ms ease-out",
                                  }}>
                                    <CheckCircleOutlineIcon sx={{ color: primary, fontSize: 20 }} />
                                  </Box>
                                  <Typography variant="subtitle1" fontWeight={800} color={isSel ? primary : "text.primary"} mb={0.5}>
                                    {tierLabel(tier)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" mb={2} sx={{ minHeight: "2.8em", whiteSpace: "pre-line" }}>
                                    {TIER_META[tier]?.description}
                                  </Typography>
                                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                    <Typography fontWeight={700} variant="h6" color={isSel ? "primary.main" : "text.primary"}>
                                      ${priceFor(tier, billing).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      / {billing === "monthly" ? "month" : "year"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                </>
              ))}

              {/* Step 2: Who's it for? */}
              {sectionCard(2, "WHO'S IT FOR?", (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Recipient's name"
                      id="name" name="name"
                      value={formik.values.name}
                      error={Boolean(formik.touched.name && formik.errors.name)}
                      helperText={(formik.touched.name && formik.errors.name) as string}
                      InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon sx={{ color: primary, fontSize: 20 }} /></InputAdornment> }}
                      onBlur={formik.handleBlur}
                      onChange={(e) => { formik.handleChange(e); formik.errors.name = ""; }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Recipient's email"
                      id="email" name="email"
                      value={formik.values.email}
                      error={Boolean(formik.touched.email && formik.errors.email)}
                      helperText={(formik.touched.email && formik.errors.email) as string}
                      InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: primary, fontSize: 20 }} /></InputAdornment> }}
                      onBlur={(e) => { formik.handleBlur(e); checkRecipientStatus(e.target.value); }}
                      onChange={(e) => { formik.handleChange(e); formik.errors.email = ""; setRecipientHasSub(false); }}
                    />
                    {recipientHasSub && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        This person already has an active subscription, so your gift will be added to their account as credit and applied to their future invoices.
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth multiline rows={3}
                      label="Add a personal message (500 char max)"
                      id="message" name="message"
                      value={formik.values.message}
                      inputProps={{ maxLength: 500 }}
                      error={Boolean(formik.touched.message && formik.errors.message)}
                      helperText={formik.touched.message && formik.errors.message ? formik.errors.message as string : `${formik.values.message.length} / 500`}
                      onBlur={formik.handleBlur}
                      onChange={(e) => { formik.handleChange(e); formik.errors.message = ""; }}
                    />
                  </Grid>
                </Grid>
              ))}

              {/* Step 3: Delivery */}
              {sectionCard(3, "WHEN SHOULD WE DELIVER IT?", (
                <Box>
                  <FormControlLabel
                    sx={{ mb: 1.5 }}
                    control={
                      <Checkbox
                        name="send" checked={formik.values.send}
                        sx={{ color: primary, "&.Mui-checked": { color: primary } }}
                        onChange={(e) => {
                          formik.setFieldValue("send", e.target.checked);
                          formik.setFieldValue("date", e.target.checked ? dayjs() : null);
                          formik.errors.date = "";
                        }}
                      />
                    }
                    label={<Typography variant="body2" fontWeight={500} sx={{ textTransform: "none" }}>Send the gift right now</Typography>}
                  />
                  {!formik.values.send && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={600} mb={1} sx={{ textTransform: "none" }}>
                        Or schedule a delivery date
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          disablePast disabled={formik.values.send}
                          sx={{ width: "100%" }} format="MM / DD / YYYY"
                          value={formik.values.date}
                          onChange={(val) => { formik.setFieldValue("date", val); formik.errors.date = ""; }}
                          slotProps={{ textField: { error: Boolean(formik.errors.date), helperText: formik.errors.date as string } }}
                        />
                      </LocalizationProvider>
                      <Typography variant="caption" color="text.secondary" mt={0.75} display="block">
                        We'll email the gift to your recipient on this date with your message and a one-tap link to start watching.
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}

              {/* Mobile CTA */}
              {isMobile && (
                <Box sx={{ mb: 3 }}>
                  <button type="submit" disabled={isLoadingData || !pricesReady}
                    style={{ width: "100%", cursor: isLoadingData || !pricesReady ? "not-allowed" : "pointer", margin: 0, opacity: !pricesReady ? 0.6 : 1 }}
                    className={configData.buttonCommonClass}>
                    {isLoadingData ? "Processing…" : !pricesReady ? "Loading prices…" : "Checkout"}
                  </button>
                </Box>
              )}
            </Grid>

            {/* GIFT SUMMARY SIDEBAR (desktop) */}
            <Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "block" } }}>
              <Box sx={{ position: "sticky", top: 88 }}>
                <Paper elevation={2} sx={{ borderRadius: "16px", overflow: "hidden", bgcolor: "background.paper" }}>

                  {/* Header bar */}
                  <Box sx={{ bgcolor: primary, px: 3, py: 2 }}>
                    <Typography variant="h6" fontWeight={700} color={tertiary} textAlign="center">
                      Gift Summary
                    </Typography>
                  </Box>

                  {/* White card body */}
                  <Box sx={{ px: 2.5, py: 2.5 }}>

                    {/* Inner dark rounded preview card — logo text + emoji + plan label */}
                    <Box sx={{
                      bgcolor: primary, borderRadius: "12px",
                      px: 2.5, py: 2.5, mb: 2.5,
                      textAlign: "center", position: "relative",
                    }}>
                      <Box sx={{ position: "absolute", top: 12, right: 14, fontSize: "1.6rem", lineHeight: 1 }}>🎁</Box>
                      <img
                        src={configData.logoUrl}
                        alt={configData.personalChannel}
                        style={{ height: 38, objectFit: "contain", maxWidth: 140, display: "block", margin: "0 auto" }}
                      />
                      <Typography variant="body2" fontWeight={600} color={tertiary} textTransform="uppercase" letterSpacing={1.5} display="block" mt={0.75}>
                        {`${activeLabel} · ${termCount} ${billing === "annual" ? (termCount > 1 ? "Years" : "Year") : (termCount > 1 ? "Months" : "Month")}`}
                      </Typography>
                    </Box>

                    {/* Recipient preview block */}
                    {(formik.values.name || formik.values.message) && (
                      <Box sx={{ p: 2, mb: 2.5, border: "1px solid", borderColor: "divider", borderRadius: "10px" }}>
                        <Typography variant="caption" color="text.secondary" display="block">To</Typography>
                        {formik.values.name && (
                          <Typography variant="body2" fontWeight={700} mt={0.25}>{formik.values.name}</Typography>
                        )}
                        {formik.values.message && (
                          <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{
                            overflow: "hidden", textOverflow: "ellipsis",
                            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                          }}>
                            "{formik.values.message}"
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Line items — normal sentence case, no uppercase */}
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>
                        {`${activeLabel} Plan`}
                      </Typography>
                      <Typography variant="body2" sx={{ textTransform: "none" }}>
                        {`${termCount} ${billing === "annual" ? (termCount > 1 ? "years" : "year") : (termCount > 1 ? "months" : "month")}`}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>Delivery</Typography>
                      <Typography variant="body2" sx={{ textTransform: "none" }}>
                        {formik.values.send
                          ? "Immediately"
                          : formik.values.date
                          ? dayjs(formik.values.date).format("MMM D, YYYY")
                          : "—"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" mb={2}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>Recipient redeems</Typography>
                      <Typography variant="body2" sx={{ textTransform: "none" }}>{recipientHasSub ? "Account credit" : "One-tap link"}</Typography>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>Subtotal</Typography>
                      {pricesReady ? (
                        <Typography variant="body2" sx={{ textTransform: "none" }}>{`$${planPrice.toFixed(2)}`}</Typography>
                      ) : pricesError ? (
                        <Typography variant="body2" sx={{ textTransform: "none" }}>—</Typography>
                      ) : (
                        <Skeleton width={64} />
                      )}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" mb={2.5}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>Estimated taxes</Typography>
                      <Typography variant="body2" sx={{ textTransform: "none" }}>$0</Typography>
                    </Stack>

                    {/* Total today — both sides bold */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                      <Typography variant="subtitle1" fontWeight={800}>Total today</Typography>
                      {pricesReady ? (
                        <Typography fontWeight={800} color={primary} sx={{ fontSize: "1.35rem", lineHeight: 1 }}>
                          {`$${planPrice.toFixed(2)}`}
                        </Typography>
                      ) : pricesError ? (
                        <Typography fontWeight={800} color={primary} sx={{ fontSize: "1.35rem", lineHeight: 1 }}>—</Typography>
                      ) : (
                        <Skeleton width={90} height={28} />
                      )}
                    </Stack>

                    {/* HTPTV-636: pricing must load before checkout so the total matches the charge */}
                    {pricesError && (
                      <Typography variant="caption" color="error" display="block" mb={1} sx={{ textTransform: "none" }}>
                        Couldn't load current pricing.{" "}
                        <Box component="span" onClick={loadOptions} sx={{ textDecoration: "underline", cursor: "pointer", fontWeight: 700 }}>
                          Retry
                        </Box>
                      </Typography>
                    )}

                    {/* Full-width CHECKOUT button */}
                    <button type="submit" disabled={isLoadingData || !pricesReady}
                      style={{ width: "100%", cursor: isLoadingData || !pricesReady ? "not-allowed" : "pointer", margin: 0, opacity: !pricesReady ? 0.6 : 1 }}
                      className={configData.buttonCommonClass}>
                      {isLoadingData ? "Processing…" : !pricesReady ? "Loading prices…" : "CHECKOUT"}
                    </button>

                    {/* Disclaimer */}
                    <Typography variant="caption" color="text.secondary" display="block" mt={2} lineHeight={1.6}>
                      {recipientHasSub
                        ? `You'll be charged today. Because your recipient already has an active subscription, your gift will be added to their ${configData.personalChannel} account as credit and applied to their future invoices. Non-refundable, no cash value.`
                        : `You'll be charged today. The gift is delivered on your chosen date, and applies to a new or existing ${configData.personalChannel} account when your recipient redeems it. Non-refundable, no cash value.`}
                    </Typography>

                  </Box>
                </Paper>
              </Box>
            </Grid>

          </Grid>
        </form>
        <SentGifts />
      </Box>

      {/* Fixed mobile bottom bar */}
      <Box sx={{
        display: { xs: "flex", md: "none" },
        flexDirection: "column",
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1300,
        bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider",
        px: 2, pt: 1.25, pb: 2,
        boxShadow: 3,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25}>
          <Stack>
            <Typography variant="caption" color="text.secondary" lineHeight={1}>Total today</Typography>
            {pricesReady ? (
              <Typography fontWeight={800} color={primary} sx={{ fontSize: "1.3rem", lineHeight: 1.1 }}>
                {`$${planPrice.toFixed(2)}`}
              </Typography>
            ) : pricesError ? (
              <Typography fontWeight={800} color={primary} sx={{ fontSize: "1.3rem", lineHeight: 1.1 }}>—</Typography>
            ) : (
              <Skeleton width={80} height={26} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" textAlign="right">
            {`${activeLabel} · ${billing === "annual" ? "Annual" : "Monthly"}`}
          </Typography>
        </Stack>
        <button type="submit" form="gift-form" disabled={isLoadingData || !pricesReady}
          style={{ width: "100%", cursor: isLoadingData || !pricesReady ? "not-allowed" : "pointer", margin: 0, opacity: !pricesReady ? 0.6 : 1 }}
          className={configData.buttonCommonClass}
          onClick={() => formik.handleSubmit()}>
          {isLoadingData ? "Processing…" : !pricesReady ? "Loading prices…" : "CHECKOUT"}
        </button>
      </Box>
    </Box>
  );
};
export default GiftPage;
